import { Box, TextField, Typography } from "@mui/material";
import { ScoreSelect } from "shared-ui/Score/ScoreSelect";
import { useHasFeature } from "shared-ui/Feature";
import { Dispatch } from "react";
import { MealType, ScoringSystemFragment } from "../../../types";
import { useIsFoodFormRedesign } from "../../FoodFormUi/FoodFormUiContext";
import { FoodCategorySelect } from "../../FoodCategory/FoodCategorySelect";
import MealTypeMultiSelect from "../../Meal/TypeMultiSelect";
import { RecipeFormTimeInMinutesInput } from "./TimeInMinutesInput";
import { canSaveRecipeFormTooltips, formValidationMessages, RecipeFormAction, RecipeFormState } from "./utils";

interface DetailsProps {
  state: RecipeFormState;
  dispatch: Dispatch<RecipeFormAction>;
  scoringSystem?: ScoringSystemFragment | null;
  disabled?: boolean;
  forStaff?: boolean;
}

const Details = ({ dispatch, state, scoringSystem, disabled, forStaff }: DetailsProps) => {
  const isRedesign = useIsFoodFormRedesign();
  const hasFoodCategory = useHasFeature("foodCategory");
  const canSaveTooltips = canSaveRecipeFormTooltips(state);
  const { name } = state;

  const handleChangeMealTypes = (mealTypes: readonly MealType[]) => {
    dispatch({
      type: "CHANGE_MEAL_TYPES",
      value: mealTypes,
    });
  };

  const isValidRecipeName = canSaveTooltips.includes(formValidationMessages.RECIPE_NAME) && state.saveClicked;

  const recipeNameField = (
    <TextField
      disabled={disabled}
      label="Recipe Name"
      value={name}
      sx={{ width: isRedesign ? 400 : 360, flexGrow: isRedesign ? 1 : 0 }}
      onChange={e =>
        dispatch({
          type: "CHANGE_NAME",
          value: e.target.value,
        })
      }
      error={isValidRecipeName}
      helperText={isValidRecipeName ? "Required" : undefined}
    />
  );

  const mealTypesField = (
    <MealTypeMultiSelect
      disabled={disabled}
      value={state.mealTypes}
      onChange={handleChangeMealTypes}
      sx={{
        width: 200,
      }}
    />
  );

  const prepTimeField = (
    <RecipeFormTimeInMinutesInput
      disabled={disabled}
      sx={{
        width: 120,
      }}
      label="Prep Time"
      value={state.prepTimeInMinutesInput}
      onChange={value =>
        dispatch({
          type: "CHANGE_PREP_TIME",
          value,
        })
      }
    />
  );

  const cookTimeField = (
    <RecipeFormTimeInMinutesInput
      disabled={disabled}
      label="Cook Time"
      value={state.cookTimeInMinutesInput}
      sx={{
        width: 120,
      }}
      onChange={value =>
        dispatch({
          type: "CHANGE_COOK_TIME",
          value,
        })
      }
    />
  );

  const foodCategoryField = hasFoodCategory && (
    <FoodCategorySelect
      disabled={disabled}
      category={state.foodCategory || null}
      onChange={value =>
        dispatch({
          type: "CHANGE_FOOD_CATEGORY",
          foodCategory: value,
        })
      }
      sx={isRedesign ? { width: 200 } : undefined}
    />
  );

  const scoreField = scoringSystem && (
    <ScoreSelect
      disabled={disabled}
      scoreValue={state.scoreValue}
      scoringSystem={scoringSystem}
      onChange={value =>
        dispatch({
          type: "CHANGE_SCORE",
          value,
        })
      }
    />
  );

  if (isRedesign) {
    return (
      <Box sx={{ gap: 2, flexDirection: "column", display: "flex" }}>
        <Typography variant="h4">Recipe Details</Typography>
        {/* Row 1: Recipe Name + Meal Types */}
        <Box sx={{ display: "flex", gap: 2 }}>
          {recipeNameField}
          {mealTypesField}
        </Box>
        {/* Row 2: Prep Time + Cook Time + Food Category + Score */}
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          {prepTimeField}
          {cookTimeField}
          {!forStaff && foodCategoryField}
          {!forStaff && scoreField}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ gap: 1, flexDirection: "column", display: "flex" }}>
      <Typography variant="h4">Recipe Details</Typography>
      <Box sx={{ display: "flex", gap: 2 }}>
        {recipeNameField}
        {mealTypesField}
        {prepTimeField}
        {cookTimeField}
        {!forStaff && scoreField}
        {!forStaff && foodCategoryField}
      </Box>
    </Box>
  );
};

export default Details;
